/**
 * Preset Theme Definitions
 * 
 * These themes are available based on tenant plan:
 * - basic: All dark themes
 * - professional: All dark + light themes
 * - enterprise: All themes + custom CSS
 */

export const PRESET_THEMES = {
  // ========== DARK THEMES (Basic+) ==========
  'dark-cyan': {
    id: 'dark-cyan',
    name: 'Dark Cyan',
    description: 'Default dark theme with cyan accents',
    isDark: true,
    minPlan: 'basic',
    colors: {
      primary: '#06b6d4',
      'primary-foreground': '#0f172a',
      secondary: '#14b8a6',
      accent: '#f59e0b',
      background: '#0f172a',
      foreground: '#e2e8f0',
      card: '#1e293b',
      'card-foreground': '#e2e8f0',
      muted: '#334155',
      'muted-foreground': '#94a3b8',
      border: '#334155',
      destructive: '#ef4444',
      success: '#22c55e'
    }
  },

  'dark-purple': {
    id: 'dark-purple',
    name: 'Dark Purple',
    description: 'Dark theme with purple accents',
    isDark: true,
    minPlan: 'basic',
    colors: {
      primary: '#a855f7',
      'primary-foreground': '#0f172a',
      secondary: '#8b5cf6',
      accent: '#f59e0b',
      background: '#0f0a1a',
      foreground: '#e2e8f0',
      card: '#1a1025',
      'card-foreground': '#e2e8f0',
      muted: '#2d2040',
      'muted-foreground': '#a78bfa',
      border: '#3b2860',
      destructive: '#ef4444',
      success: '#22c55e'
    }
  },

  'dark-emerald': {
    id: 'dark-emerald',
    name: 'Dark Emerald',
    description: 'Dark theme with green accents',
    isDark: true,
    minPlan: 'basic',
    colors: {
      primary: '#10b981',
      'primary-foreground': '#0f172a',
      secondary: '#14b8a6',
      accent: '#f59e0b',
      background: '#0a1a14',
      foreground: '#e2e8f0',
      card: '#0f2920',
      'card-foreground': '#e2e8f0',
      muted: '#1a4030',
      'muted-foreground': '#6ee7b7',
      border: '#166534',
      destructive: '#ef4444',
      success: '#22c55e'
    }
  },

  'dark-rose': {
    id: 'dark-rose',
    name: 'Dark Rose',
    description: 'Dark theme with rose/pink accents',
    isDark: true,
    minPlan: 'basic',
    colors: {
      primary: '#f43f5e',
      'primary-foreground': '#ffffff',
      secondary: '#fb7185',
      accent: '#fbbf24',
      background: '#1a0a10',
      foreground: '#e2e8f0',
      card: '#2a1520',
      'card-foreground': '#e2e8f0',
      muted: '#4a2535',
      'muted-foreground': '#fda4af',
      border: '#881337',
      destructive: '#ef4444',
      success: '#22c55e'
    }
  },

  'dark-amber': {
    id: 'dark-amber',
    name: 'Dark Amber',
    description: 'Warm dark theme with amber accents',
    isDark: true,
    minPlan: 'basic',
    colors: {
      primary: '#f59e0b',
      'primary-foreground': '#0f172a',
      secondary: '#fbbf24',
      accent: '#06b6d4',
      background: '#1a1408',
      foreground: '#e2e8f0',
      card: '#2a2210',
      'card-foreground': '#e2e8f0',
      muted: '#4a3a1a',
      'muted-foreground': '#fcd34d',
      border: '#854d0e',
      destructive: '#ef4444',
      success: '#22c55e'
    }
  },

  // ========== LIGHT THEMES (Professional+) ==========
  'light-blue': {
    id: 'light-blue',
    name: 'Light Blue',
    description: 'Clean light theme with blue accents',
    isDark: false,
    minPlan: 'professional',
    colors: {
      primary: '#2563eb',
      'primary-foreground': '#ffffff',
      secondary: '#3b82f6',
      accent: '#f59e0b',
      background: '#f8fafc',
      foreground: '#1e293b',
      card: '#ffffff',
      'card-foreground': '#1e293b',
      muted: '#e2e8f0',
      'muted-foreground': '#64748b',
      border: '#cbd5e1',
      destructive: '#dc2626',
      success: '#16a34a'
    }
  },

  'light-slate': {
    id: 'light-slate',
    name: 'Light Slate',
    description: 'Minimal light theme with slate tones',
    isDark: false,
    minPlan: 'professional',
    colors: {
      primary: '#475569',
      'primary-foreground': '#ffffff',
      secondary: '#64748b',
      accent: '#06b6d4',
      background: '#f1f5f9',
      foreground: '#0f172a',
      card: '#ffffff',
      'card-foreground': '#0f172a',
      muted: '#e2e8f0',
      'muted-foreground': '#64748b',
      border: '#cbd5e1',
      destructive: '#dc2626',
      success: '#16a34a'
    }
  },

  // ========== PREMIUM THEMES (Enterprise) ==========
  'gradient-aurora': {
    id: 'gradient-aurora',
    name: 'Aurora',
    description: 'Premium gradient theme with northern lights colors',
    isDark: true,
    minPlan: 'enterprise',
    colors: {
      primary: '#06b6d4',
      'primary-foreground': '#0f172a',
      secondary: '#a855f7',
      accent: '#22c55e',
      background: '#030712',
      foreground: '#e2e8f0',
      card: '#111827',
      'card-foreground': '#e2e8f0',
      muted: '#1f2937',
      'muted-foreground': '#9ca3af',
      border: '#374151',
      destructive: '#ef4444',
      success: '#22c55e',
      gradient: 'linear-gradient(135deg, #06b6d4, #a855f7, #22c55e)'
    }
  },

  'gradient-sunset': {
    id: 'gradient-sunset',
    name: 'Sunset',
    description: 'Warm gradient theme with sunset colors',
    isDark: true,
    minPlan: 'enterprise',
    colors: {
      primary: '#f97316',
      'primary-foreground': '#0f172a',
      secondary: '#f43f5e',
      accent: '#fbbf24',
      background: '#0a0505',
      foreground: '#e2e8f0',
      card: '#1a0f0f',
      'card-foreground': '#e2e8f0',
      muted: '#2a1a1a',
      'muted-foreground': '#fdba74',
      border: '#4a2020',
      destructive: '#ef4444',
      success: '#22c55e',
      gradient: 'linear-gradient(135deg, #f97316, #f43f5e, #fbbf24)'
    }
  }
};

/**
 * Plan hierarchy for theme access
 */
export const PLAN_HIERARCHY = {
  basic: 0,
  professional: 1,
  enterprise: 2
};

/**
 * Get themes available for a specific plan
 */
export function getThemesForPlan(plan) {
  const planLevel = PLAN_HIERARCHY[plan] || 0;
  
  return Object.values(PRESET_THEMES).filter(theme => {
    const themeLevel = PLAN_HIERARCHY[theme.minPlan] || 0;
    return planLevel >= themeLevel;
  });
}

/**
 * Check if a theme is available for a plan
 */
export function isThemeAvailable(themeId, plan) {
  const theme = PRESET_THEMES[themeId];
  if (!theme) return false;
  
  const planLevel = PLAN_HIERARCHY[plan] || 0;
  const themeLevel = PLAN_HIERARCHY[theme.minPlan] || 0;
  
  return planLevel >= themeLevel;
}

/**
 * Get default theme for a plan
 */
export function getDefaultTheme(plan) {
  return PRESET_THEMES['dark-cyan'];
}

export default PRESET_THEMES;
